/**
 * Enrol people (or submissions) from a segment into a workflow: once now,
 * or on a schedule (every day, or every week on a day, at a time, UK time).
 *
 * Opened from a workflow (its record type fixed), or from a segment beside
 * the leads list (the segment fixed; the workflow chosen here). A segment is
 * one of your own saved tabs, one shared with you, or the filter on screen;
 * its filters go to the server in the search body's shape, exactly as the
 * leads list and segment counts send them, and the server builds the rows
 * as you, with your scope.
 *
 * Before anything is enrolled, a preview says how many: "This will enrol
 * 1,240 people". A schedule enrols only those who newly match each time,
 * unless "re-enrol every time" is on.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Group, Loader, Modal, SegmentedControl, Select, Stack, Switch, Text, TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUsersPlus } from '@tabler/icons-react';
import {
  segmentFilters, useFilterTabs, useSharedSegments, type FilterTab, type LeadFilters,
} from '../../api/queries';
import {
  useEnrolEdits, useEnrolPreview, useWorkflows, type EnrolBody, type EnrolSegmentKind, type RecordType,
} from '../../api/workflows';
import { useVocab } from '../../vocab';

export type EnrolSegmentChoice = {
  kind: EnrolSegmentKind;
  id?: string | null;
  name: string;
  filter: LeadFilters;
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** The segments a person can enrol from: their own tabs, then the shared ones. */
export function useSegmentChoices(opened: boolean): EnrolSegmentChoice[] {
  const tabs = useFilterTabs(opened).data?.tabs;
  const shared = useSharedSegments(opened).data?.segments;
  return useMemo(() => [
    ...(tabs ?? []).map((t: FilterTab) => ({ kind: 'private' as const, id: String(t.id), name: t.name, filter: segmentFilters(t) })),
    ...(shared ?? []).map((s) => ({
      kind: 'shared' as const,
      id: s.id,
      name: s.name,
      filter: segmentFilters({ id: 0, name: s.name, has_changes: false, filter_state: s.filter_state } as FilterTab),
    })),
  ], [tabs, shared]);
}

export const keyOf = (s: EnrolSegmentChoice) => `${s.kind}:${s.id ?? ''}`;

export function EnrolDialog({
  opened, onClose, workflowId, recordType, segment, defaultRecordType = 'person',
}: {
  opened: boolean;
  onClose: () => void;
  /** Fixed when opened from a workflow. */
  workflowId?: string | null;
  recordType?: RecordType;
  /** Fixed when opened from a segment or the filter on screen. */
  segment?: EnrolSegmentChoice | null;
  defaultRecordType?: RecordType;
}) {
  const v = useVocab();
  const list = useWorkflows(opened);
  const choices = useSegmentChoices(opened && !segment);
  const edits = useEnrolEdits();
  const [rt, setRt] = useState<RecordType>(recordType ?? defaultRecordType);
  const [wid, setWid] = useState<string | null>(workflowId ?? null);
  const [segKey, setSegKey] = useState<string | null>(segment ? keyOf(segment) : null);
  const [mode, setMode] = useState<'once' | 'schedule'>('once');
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>('daily');
  const [weekday, setWeekday] = useState('0');
  const [time, setTime] = useState('09:00');
  const [reenrol, setReenrol] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!opened) return;
    setRt(recordType ?? defaultRecordType);
    setWid(workflowId ?? null);
    setSegKey(segment ? keyOf(segment) : null);
    setMode('once');
    setError('');
  }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

  const workflows = (list.data?.workflows ?? []).filter((w) => w.enabled && w.record_type === rt);
  const chosen = segment ?? choices.find((c) => keyOf(c) === segKey) ?? null;
  const body: EnrolBody | null = wid && chosen
    ? {
      record_type: rt,
      segment: { kind: chosen.kind, id: chosen.id ?? null },
      filter: chosen.filter as EnrolBody['filter'],
      schedule: mode === 'schedule'
        ? { frequency, weekday: frequency === 'weekly' ? Number(weekday) : null, time, reenrol }
        : null,
    }
    : null;
  const previewBody = body ? { ...body, schedule: null } : null;
  const preview = useEnrolPreview(opened ? wid : null, previewBody);
  const timeOk = /^([01]?\d|2[0-3]):[0-5]\d$/.test(time);

  async function go() {
    if (!body || !wid) return;
    setError('');
    try {
      const res = await edits.enrol.mutateAsync({ id: wid, body });
      if (res.schedule) {
        notifications.show({ color: 'teal', title: 'Schedule set', message: `${res.schedule.sentence}: ${res.preview.segment_name}.` });
      } else {
        notifications.show({ color: 'teal', title: 'Enrolling', message: `${res.preview.sentence} It happens in the background.` });
      }
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const many = rt === 'submission' ? v.many('lead') : v.many('person');
  return (
    <Modal opened={opened} onClose={onClose} title={`Enrol ${many}`} size="md">
      <Stack gap="sm" data-testid="enrol-dialog">
        {!recordType && (
          <SegmentedControl
            size="xs"
            data={[{ value: 'person', label: v.Many('person') }, { value: 'submission', label: v.Many('lead') }]}
            value={rt}
            onChange={(x) => { setRt(x as RecordType); setWid(null); }}
            style={{ alignSelf: 'flex-start' }}
            data-testid="enrol-record-type"
          />
        )}
        {!workflowId && (
          <Select
            size="sm"
            label={`Into the ${v.one('workflow')}`}
            placeholder={workflows.length ? `Choose a ${v.one('workflow')}` : `No ${v.many('workflow')} on ${many} are switched on`}
            data={workflows.map((w) => ({ value: w.id, label: w.name }))}
            value={wid}
            onChange={setWid}
            searchable
            data-testid="enrol-workflow"
          />
        )}
        {segment ? (
          <Text size="sm">{`From: ${segment.name}`}</Text>
        ) : (
          <Select
            size="sm"
            label="From the segment"
            placeholder="Choose a segment"
            data={[
              { group: 'Mine', items: choices.filter((c) => c.kind === 'private').map((c) => ({ value: keyOf(c), label: c.name })) },
              { group: 'Shared with me', items: choices.filter((c) => c.kind === 'shared').map((c) => ({ value: keyOf(c), label: c.name })) },
            ].filter((g) => g.items.length > 0)}
            value={segKey}
            onChange={setSegKey}
            searchable
            data-testid="enrol-segment"
          />
        )}
        <SegmentedControl
          size="xs"
          data={[{ value: 'once', label: 'Once, now' }, { value: 'schedule', label: 'On a schedule' }]}
          value={mode}
          onChange={(m) => setMode(m as 'once' | 'schedule')}
          style={{ alignSelf: 'flex-start' }}
          data-testid="enrol-mode"
        />
        {mode === 'schedule' && (
          <Stack gap="xs" data-testid="enrol-schedule">
            <Group gap="xs" align="flex-end" wrap="wrap">
              <Select
                size="xs"
                label="How often"
                data={[{ value: 'daily', label: 'Every day' }, { value: 'weekly', label: 'Every week' }]}
                value={frequency}
                allowDeselect={false}
                onChange={(x) => x && setFrequency(x as 'daily' | 'weekly')}
                w={140}
                data-testid="enrol-frequency"
              />
              {frequency === 'weekly' && (
                <Select
                  size="xs"
                  label="On"
                  data={DAYS.map((d, i) => ({ value: String(i), label: d }))}
                  value={weekday}
                  allowDeselect={false}
                  onChange={(x) => x && setWeekday(x)}
                  w={140}
                  data-testid="enrol-weekday"
                />
              )}
              <TextInput
                size="xs"
                type="time"
                label="At (UK time)"
                value={time}
                onChange={(e) => setTime(e.currentTarget.value)}
                error={timeOk ? undefined : 'HH:MM'}
                w={130}
                data-testid="enrol-time"
              />
            </Group>
            <Switch
              size="xs"
              label="Re-enrol everyone who matches, every time"
              description={`Off: each time, only ${many} who newly match since the last time.`}
              checked={reenrol}
              onChange={(e) => setReenrol(e.currentTarget.checked)}
              data-testid="enrol-reenrol"
            />
          </Stack>
        )}
        <Alert variant="light" color={preview.error ? 'red' : 'chartBlue'} p="xs" data-testid="enrol-preview">
          {!body ? (
            <Text size="sm">{`Choose a ${workflowId ? 'segment' : `${v.one('workflow')} and a segment`} to see how many.`}</Text>
          ) : preview.isPending ? (
            <Group gap="xs"><Loader size="xs" /><Text size="sm">Counting</Text></Group>
          ) : preview.error ? (
            <Text size="sm">{preview.error.message}</Text>
          ) : preview.data ? (
            <Stack gap={2}>
              <Text size="sm" fw={600}>{mode === 'schedule' ? preview.data.sentence.replace('This will enrol', 'Right now it would enrol') : preview.data.sentence}</Text>
              {preview.data.send_warning && (
                <Text size="xs" c="orange" data-testid="enrol-send-warning">{preview.data.send_warning}</Text>
              )}
              {preview.data.not_stored > 0 && (
                <Text size="xs" c="dimmed">{`${preview.data.not_stored} more are known only by an email address and cannot be enrolled until they are stored.`}</Text>
              )}
              <Text size="xs" c="dimmed">{`Anyone already in the ${v.one('workflow')}, or at its limit of 5 a day, is skipped and counted.`}</Text>
            </Stack>
          ) : null}
        </Alert>
        {error && <Alert color="red" variant="light" p="xs"><Text size="sm">{error}</Text></Alert>}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button
            leftSection={<IconUsersPlus size={16} />}
            disabled={!body || !preview.data || (mode === 'schedule' && !timeOk)}
            loading={edits.enrol.isPending}
            onClick={go}
            data-testid="enrol-go"
          >
            {mode === 'schedule' ? 'Set the schedule' : `Enrol ${many}`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
